import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchPrintSolutions } from '../components/utils/printLexical';

/**
 * usePrintSolutions — 인쇄 창의 풀이 요청 상태(시험지·오답노트 공용).
 *
 * 반환
 *  - solutions   : v4 solutions 계약. 전부 실패하면 빈 배열이다(「불러오지 못함」 면으로 채우지 않는다)
 *  - loading     : 풀이를 켰는데 아직 결과가 없는 동안 true. 렌더에서 계산하므로 켠 순간부터 빈틈이 없다
 *  - failedCount : 불러오지 못한 문제 수(「풀이 없음」은 세지 않는다)
 *  - wanted / setWanted : 풀이를 요청할지
 *  - retry       : 실패한 문제만 다시 묻는다
 *
 * 다시 묻는 기준은 배열 참조가 아니라 **문제 번호 목록**이다. 부모가 같은 문제를 새 배열로 줘도,
 * 토큰 갱신으로 fetchJson 이 바뀌어도 진행 중인 요청을 버리고 처음부터 다시 보내지 않는다.
 * 창을 닫거나 문제가 바뀌면 남은 요청을 보내지 않는다.
 */
const NO_SOLUTIONS = Object.freeze([]);
// attempted = 이번 시도의 전체 결과. 전부 실패해 solutions 가 비어도 어디까지 물었는지는 다시 시도에 넘긴다
const IDLE = Object.freeze({ runKey: null, problemKey: null, solutions: NO_SOLUTIONS, attempted: NO_SOLUTIONS, failedCount: 0 });

export default function usePrintSolutions(problems, fetchJson, open = true) {
  const [wanted, setWanted] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(IDLE);

  const problemKey = useMemo(() => problems.map((problem) => problem.problem_id).join(','), [problems]);
  const runKey = `${problemKey}#${attempt}`;

  // effect 안에서 최신값을 읽기만 한다 — 이 값들이 바뀌었다고 요청을 다시 보내지 않기 위해 의존성에서 뺀다
  const latest = useRef({ problems, fetchJson });
  latest.current = { problems, fetchJson };
  const retryBase = useRef(NO_SOLUTIONS);

  useEffect(() => {
    if (!open) setWanted(false);
    if (!open || !wanted || !problemKey) {
      setResult(IDLE);
      return undefined;
    }
    const controller = new AbortController();
    const previous = retryBase.current;
    retryBase.current = NO_SOLUTIONS;
    const targets = latest.current.problems;

    fetchPrintSolutions(targets, (url) => latest.current.fetchJson(url), { previous, signal: controller.signal })
      .then((solutions) => {
        if (controller.signal.aborted) return;
        const failedCount = solutions.filter((solution) => solution.failed).length;
        if (failedCount > 0) console.error(`풀이 ${failedCount}/${solutions.length}개를 불러오지 못했습니다.`);
        setResult({
          runKey,
          problemKey,
          solutions: failedCount === solutions.length ? NO_SOLUTIONS : solutions,
          attempted: solutions,
          failedCount,
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('풀이 데이터 fetch 실패:', error);
        setResult({ runKey, problemKey, solutions: NO_SOLUTIONS, attempted: NO_SOLUTIONS, failedCount: targets.length });
      });

    return () => controller.abort();
  }, [open, wanted, problemKey, runKey]);

  const retry = useCallback(() => {
    retryBase.current = result.attempted;
    setAttempt((value) => value + 1);
  }, [result.attempted]);

  const current = wanted && result.problemKey === problemKey;
  return {
    solutions: current ? result.solutions : NO_SOLUTIONS,
    failedCount: current ? result.failedCount : 0,
    loading: open && wanted && problemKey !== '' && result.runKey !== runKey,
    wanted,
    setWanted,
    retry,
  };
}
